import type { ProjectViewModel } from '@repo/commons/models'
import type { OutputObject } from '@semanticedge/persistence-service-noheaders-client/src'
import { ElementType, FileExplorerElementType, type Element } from '@repo/commons/models'
import type { Node } from '@xyflow/svelte'
import {
	isLeverResource,
	isReferenceTable
} from '../../features/referenceTable/utils/referenceTable'

export function getAllOutputObjectsFromProject(project: ProjectViewModel): Partial<OutputObject>[] {
	if (!project || !project.activities) return []

	const outputObjectsMap = new Map<string, Partial<OutputObject>>()

	project.activities.forEach((activity) => {
		activity.flows.forEach((flow) => {
			flow.graph.nodes.forEach((node) => {
				const outputObject = (node.data as any)?.outputObject as Partial<OutputObject> | undefined
				if (outputObject && outputObject.id && outputObject.name) {
					// Use id as key to ensure uniqueness
					if (!outputObjectsMap.has(outputObject.id)) {
						outputObjectsMap.set(outputObject.id, outputObject)
					}
				}
			})
		})
	})

	return Array.from(outputObjectsMap.values())
}

export function findElementById(
	project: ProjectViewModel,
	type: ElementType,
	id: string
): Element | null {
	switch (type) {
		case ElementType.ACTIVITY:
			return project.activities.find((activity) => activity.id === id) || null
		case ElementType.FLOW:
			return (
				project.activities.flatMap((activity) => activity.flows).find((flow) => flow.id === id) ||
				null
			)
		case ElementType.TOPIC:
			return (
				project.activities
					.flatMap((activity) => activity.topics)
					.find((topic) => topic.id === id) || null
			)
		case ElementType.REPORTING:
			return project.reportingConfiguration.find((element) => element.id === id) || null
		case ElementType.NODE:
			return (
				project.activities
					.flatMap((activity) => activity.flows)
					.flatMap((flow) => flow.graph.nodes)
					.find((node) => node.id === id) || null
			)
		case ElementType.EDGE:
			return (
				project.activities
					.flatMap((activity) => activity.flows)
					.flatMap((flow) => flow.graph.edges)
					.find((edge) => edge.id === id) || null
			)
		case ElementType.CUSTOM_TYPE:
			return project.schemas.find((schema) => schema.id === id) || null
		case ElementType.FUNCTION_CONTRACT:
			return (
				project.functionContracts.find((functionContract) => functionContract.id === id) || null
			)
		case ElementType.ADAPTER_EXCEPTION:
			return project.adapterExceptions.find((exception) => exception.id === id) || null
		case ElementType.ADAPTER_SERVICE:
			return project.adapterServices.find((service) => service.id === id) || null
		case ElementType.CONSTRAINT:
			return project.constraints.find((constraint) => constraint.id === id) || null
		case ElementType.TEST_SUITE:
			return project.testSuites.find((testSuite) => testSuite.id === id) || null
		case ElementType.USE_CASE:
			return (
				project.testSuites
					.flatMap((testSuite) => testSuite.useCases)
					.find((useCase) => useCase.id === id) || null
			)
		case ElementType.LEVER:
			return (
				project.resourceConfiguration.find(
					(resource) => resource.id === id && isLeverResource(resource)
				) || null
			)
		case ElementType.REFERENCE_TABLE:
			return (
				project.resourceConfiguration.find(
					(resource) => resource.id === id && isReferenceTable(resource)
				) || null
			)
		case ElementType.RESOURCE_SCHEMA:
			return project.schemas?.find((schema) => schema.id === id) || null
		case ElementType.GSP:
			return project.projectParameters.find((gsp) => gsp.id === id) || null
		case ElementType.FLOW_EVENTS:
			return id === FileExplorerElementType.FLOW_EVENTS
				? { id: FileExplorerElementType.FLOW_EVENTS, flowEvents: project.flowEvents }
				: null
		case ElementType.INSTANCE_ITEM:
			return project.instanceConfigurations[id] || null
		default:
			return null
	}
}
